import { create } from 'zustand';
import type { AuthUser, LoginResponse, PermissionCode } from '@odr/shared';

import { api, tokens } from './api';
import { clearAll } from './offline/db';

interface AuthState {
  user: AuthUser | null;
  status: 'loading' | 'authenticated' | 'anonymous';
  signIn: (username: string, password: string) => Promise<AuthUser>;
  signOut: () => Promise<void>;
  restore: () => Promise<void>;
  setUser: (user: AuthUser) => void;
}

export const useAuth = create<AuthState>((set) => ({
  user: null,
  status: 'loading',

  async signIn(username, password) {
    const response = await api.post<LoginResponse>('/api/auth/login', {
      username: username.trim(),
      password,
    });
    tokens.set(response.accessToken, response.refreshToken);
    // Clear any cached state left by a previous operator on a shared tablet.
    await clearAll();
    set({ user: response.user, status: 'authenticated' });
    return response.user;
  },

  async signOut() {
    const refreshToken = tokens.refresh();
    try {
      if (refreshToken) await api.post('/api/auth/logout', { refreshToken });
    } catch {
      // Signing out locally must succeed even with no network.
    }
    tokens.clear();
    await clearAll();
    set({ user: null, status: 'anonymous' });
  },

  async restore() {
    if (!tokens.access()) {
      set({ status: 'anonymous', user: null });
      return;
    }
    try {
      const user = await api.get<AuthUser>('/api/auth/me');
      set({ user, status: 'authenticated' });
    } catch {
      // why: do NOT sign the operator out here. A failed /me while offline is
      // expected — the app must keep working from cache. Only an explicit 401
      // (handled centrally in api.ts) ends the session.
      set((state) =>
        state.user
          ? { status: 'authenticated' }
          : { status: tokens.access() ? 'authenticated' : 'anonymous' },
      );
    }
  },

  setUser(user) {
    set({ user, status: 'authenticated' });
  },
}));

/** Client-side permission check — for rendering only, never for access control. */
export function useCan(permission: PermissionCode): boolean {
  return useAuth((state) => state.user?.permissions.includes(permission) ?? false);
}

export function useCanAny(...permissions: PermissionCode[]): boolean {
  return useAuth(
    (state) => permissions.some((p) => state.user?.permissions.includes(p)) ?? false,
  );
}

export function useHasRole(...roles: string[]): boolean {
  return useAuth((state) => roles.some((r) => state.user?.roles.includes(r)) ?? false);
}

export function can(user: AuthUser | null, permission: PermissionCode): boolean {
  return user?.permissions.includes(permission) ?? false;
}

/** Where a user should land after sign-in, based on what they actually do. */
export function homeRouteFor(user: AuthUser | null): string {
  if (!user) return '/login';
  const has = (p: string) => user.permissions.includes(p);
  if (has('dashboard.operator') && user.roles.includes('OPERATOR')) return '/';
  if (has('dashboard.supervisor')) return '/supervisor';
  if (has('dashboard.coach')) return '/coach';
  if (has('dashboard.maintenance')) return '/maintenance';
  if (has('dashboard.management')) return '/management';
  if (has('user.manage')) return '/admin';
  return '/';
}
